import {
  FloatingFormItem,
  InputDate,
  InputDecimal,
  InputText,
} from '@/components/_common';
import {
  useCreateNfsInstallment,
  useNfsInstallment,
  useUpdateNfsInstallment,
} from '@/core/domain/invoice/NfsInstallments/Installment.hook';
import {
  CreateNfsInstallment,
  InstallmentsNFSEPaymentType,
  NfsInstallmentFormValues,
  UpdateNfsInstallment,
} from '@/core/domain/invoice/NfsInstallments/Installment.types';
import { DateTimeFormat } from '@/utils/moment';
import { Button, Col, Form, Modal, ModalProps, Row, Select } from 'antd';
import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';

interface Props extends Omit<ModalProps, 'onCancel'> {
  installmentId?: number;
  invoiceId: number;
  onCancel: () => void;
}

const NfsInstallmentModalForm: React.FC<Props> = ({
  open,
  onCancel,
  installmentId,
  invoiceId,
}) => {
  const intl = useIntl();

  const [form] = Form.useForm<NfsInstallmentFormValues>();

  const createMutation = useCreateNfsInstallment();
  const updateMutation = useUpdateNfsInstallment();
  const { data: detailInstallment, mutateAsync } = useNfsInstallment();

  useEffect(() => {
    if (installmentId) {
      mutateAsync({
        id: installmentId,
        invoiceId,
      });
    }
  }, [installmentId]);

  useEffect(() => {
    if (detailInstallment) {
      form.setFieldsValue({ ...detailInstallment });
    }
  }, [detailInstallment]);

  const optionsPaymentType = [
    {
      value: InstallmentsNFSEPaymentType.CASH,
      label: 'Dinheiro',
    },
    {
      value: InstallmentsNFSEPaymentType.DEADLINE,
      label: 'Prazo',
    },
    {
      value: InstallmentsNFSEPaymentType.PRESENTATION,
      label: 'Apresentação',
    },
    {
      value: InstallmentsNFSEPaymentType.DEBIT,
      label: 'Débito',
    },
    {
      value: InstallmentsNFSEPaymentType.CREDIT,
      label: 'Crédito',
    },
  ];

  const handleCancel = () => {
    form.resetFields();

    onCancel();
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();

    if (installmentId) {
      const obj: UpdateNfsInstallment = {
        ...values,
        dueDate: values.dueDate.format(DateTimeFormat),
        invoiceId,
        id: installmentId,
      };

      await updateMutation.mutateAsync(obj);

      handleCancel();
    } else {
      const obj: CreateNfsInstallment = {
        ...values,
        dueDate: values.dueDate.format(DateTimeFormat),
        invoiceId,
      };

      await createMutation.mutateAsync(obj);

      handleCancel();
    }
  };

  return (
    <Modal
      title={`${!!installmentId ? 'Edição' : 'Criação'} da Parcela`}
      open={open}
      onCancel={handleCancel}
      width={400}
      footer={[
        <Button type="primary" ghost onClick={handleCancel}>
          {intl.formatMessage({ id: 'commons.modal.button.cancel' })}
        </Button>,
        <Button type="primary" onClick={handleSubmit}>
          {installmentId ? 'Editar' : 'Criar'}
        </Button>,
      ]}
    >
      <Form form={form}>
        <Row gutter={[12, 12]}>
          <Col span={24}>
            <FloatingFormItem name="paymentType" label="Tipo de pagamento">
              <Select options={optionsPaymentType} />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <FloatingFormItem name="dueDate" label="Data de vencimento">
              <InputDate />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <FloatingFormItem name="number" label="Número">
              <InputText onlyNumber />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <FloatingFormItem name="value" label="Valor (R$)" initialValue={0}>
              <InputDecimal />
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default NfsInstallmentModalForm;
